import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { m } from 'framer-motion';
import { useSnackbar } from 'notistack';
// @mui
import { styled } from '@mui/material/styles';
import { Stack, Container, Grid } from '@mui/material';
// hooks
import useLocales from '../../hooks/useLocales';
import useAuth from '../../hooks/useAuth';
// layouts
import LanguagePopover from '../../layouts/dashboard/header/LanguagePopover';
// components
import Page from '../../components-custom/Page';
import Logo from '../../components-custom/Logo';
import LoadingLogo from '../../components-custom/LoadingLogo';
import { MotionContainer, varFade } from '../../components/animate';
import Iconify from '../../components/Iconify';
import { track } from '../../components-custom/Analytics';
// utils
import localizeValue from '../../utils/localizeValue';
import { errorHandler } from '../../utils/errorHandler';

// ----------------------------------------------------------------------

const LoadingAnimationConfig = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 2, ease: [0.43, 0.13, 0.23, 0.96], repeat: Infinity, repeatDelay: 1 },
  },
  exit: {
    opacity: 0,
    transition: { duration: 2, ease: [0.43, 0.13, 0.23, 0.96], repeat: Infinity, repeatDelay: 1 },
  },
};

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const HeaderStyle = styled('header')(({ theme }) => ({
  top: 0,
  zIndex: 9,
  lineHeight: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  padding: theme.spacing(3),
  justifyContent: 'space-between',
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
    padding: theme.spacing(7, 5, 0, 7),
  },
}));

const ContentStyle = styled('div')(({ theme }) => ({
  margin: 'auto',
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(12, 0),
}));

const MAStyle = styled(m.div)(({ theme }) => ({
  cursor: 'pointer',
  display: 'block',
  color: theme.palette.text.disabled,
  '&:hover': { color: theme.palette.primary.main },
}));

// ----------------------------------------------------------------------

const INDUSTRIES = [
  { id: 'GroceriesSupermarketsTakeawayFood', icon: 'ic:outline-local-grocery-store', size: 38 },
  { id: 'ReadyMadeClothes', icon: 'ic:outline-checkroom', size: 28 },
  { id: 'HouseholdElectronicElectricalAppliances', icon: 'ic:outline-live-tv', size: 24 },
  { id: 'ConstructionMaterials', icon: 'ic:baseline-handyman', size: 34 },
  { id: 'Construction', icon: 'ic:outline-other-houses', size: 46 },
  { id: 'Furniture', icon: 'ic:outline-chair', size: 32 },
  { id: 'LawFirmsLegalConsulting', icon: 'ic:baseline-balance', size: 24 },
  { id: 'AutomotiveMaintenanceSpareParts', icon: 'ic:outline-directions-car', size: 28 },
  { id: 'BlacksmithCarpentryAluminumWorkshops', icon: 'ic:outline-carpenter', size: 32 },
  { id: 'EngineeringOffices', icon: 'ic:sharp-engineering', size: 52 },
  { id: 'RentingSellingRealEstate', icon: 'ic:outline-real-estate-agent', size: 52 },
  { id: 'other', icon: 'ic:baseline-design-services', size: 58 },
];

// ----------------------------------------------------------------------

NameView.propTypes = {
  state: PropTypes.string,
};

function NameView({ state }) {
  const { translate } = useLocales();
  const { user } = useAuth();
  return (
    <MotionContainer component={m.h1} sx={{ typography: 'h1', display: 'flex', overflow: 'hidden' }}>
      {translate('auth.demo.hello', { name: localizeValue(user.name) })
        .split(' ')
        .map((word, index) => (
          <m.span key={index} variants={state === 'name-start' ? varFade().in : varFade().out}>
            {word}&nbsp;
          </m.span>
        ))}
    </MotionContainer>
  );
}

IndustriesView.propTypes = {
  state: PropTypes.string,
  onIndustryClick: PropTypes.func,
};

function IndustriesView({ state, onIndustryClick }) {
  const { translate } = useLocales();
  return (
    <>
      <MotionContainer component={m.h2} sx={{ typography: 'h1', textAlign: 'center', overflow: 'hidden', mb: 6 }}>
        <m.span variants={state === 'options-start' ? varFade().in : varFade().out} style={{ lineHeight: 1.5 }}>
          {translate('auth.demo.industries.root')}
        </m.span>
      </MotionContainer>
      <MotionContainer component={m.div} sx={{ typography: 'h3', textAlign: 'center', overflow: 'hidden' }}>
        <Grid container spacing={6}>
          {INDUSTRIES.map((industry, index) => (
            <Grid item key={index} xs={12} md={4}>
              <MAStyle
                variants={state === 'options-start' ? varFade().in : varFade().out}
                onClick={() => onIndustryClick(industry.id)}
                sx={{ fontSize: 24, textAlign: 'center', color: index > 10 ? '#4a5158' : null }}
              >
                <Stack direction="column" alignItems="center" justifyContent="center">
                  <Iconify icon={industry.icon} width={42} height={42} sx={{ display: 'block', textAlign: 'center' }} />
                  {translate(`auth.demo.industries.${industry.id}`)}
                </Stack>
              </MAStyle>
            </Grid>
          ))}
        </Grid>
      </MotionContainer>
    </>
  );
}

// ----------------------------------------------------------------------

export default function Demo() {
  const { translate } = useLocales();
  const { user, createDemo, checkDemo } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState(null); // name-start, name-end, options-start, options-end

  useEffect(() => {
    if (user.demoable === 'wait') {
      setState(null);
      setTimeout(() => {
        checkDemoDone();
      }, 1000);
    } else {
      track.event('Demo.start');
      setTimeout(() => {
        setState('name-start');
        setTimeout(() => {
          setState('name-end');
          setTimeout(() => {
            setState('options-start');
          }, 500);
        }, 500);
      }, 500);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onIndustryClick = (industry) => {
    setState('options-end');
    // setTimeout(() => {
    setState(null);
    onSubmit(industry);
    // }, 500);
  };

  const onSubmit = async (industry) => {
    try {
      track.event(`Demo.choose.${industry}`);
      const request = await createDemo({ industry });
      if (request?.success) {
        // setTimeout(() => {
        checkDemoDone();
        // }, 3000);
      }
    } catch (error) {
      enqueueSnackbar(errorHandler(error, translate('auth.demo.message.submit.error')), { variant: 'error' });
    }
  };

  const checkDemoDone = async () => {
    try {
      const request = await checkDemo();
      if (request?.success) {
        track.event('Demo.end');
      }
    } catch (error) {
      enqueueSnackbar(errorHandler(error, translate('auth.demo.message.submit.error')), { variant: 'error' });
    }
  };

  return (
    <Page title={translate('pageTitle', { page: translate('auth.demo.pageName') })}>
      <RootStyle>
        <HeaderStyle>
          <Logo />
          <LanguagePopover />
        </HeaderStyle>
        <Container maxWidth="lg">
          <ContentStyle>
            <Stack direction="column" alignItems="center" justifyContent="center">
              {state ? (
                <>
                  {state.includes('name') && <NameView key={state} state={state} />}
                  {state.includes('options') && (
                    <IndustriesView key={state} state={state} onIndustryClick={onIndustryClick} />
                  )}
                </>
              ) : (
                <>
                  <LoadingLogo />
                  <MotionContainer
                    component={m.p}
                    sx={{ typography: 'subtitle1', color: 'secondary.main', display: 'flex', overflow: 'hidden' }}
                  >
                    {translate('auth.demo.loading')
                      .split(' ')
                      .map((word, index) => (
                        <m.span key={index} variants={LoadingAnimationConfig}>
                          {word}&nbsp;
                        </m.span>
                      ))}
                  </MotionContainer>
                </>
              )}
            </Stack>
          </ContentStyle>
        </Container>
      </RootStyle>
    </Page>
  );
}
